<!-- eslint-disable vue/valid-v-slot -->
<template>
  <v-card flat>
    <v-card-title class="title font-weigth-light px-0 py-0 mb-4">
      Vehicle Booking Details
    </v-card-title>
    <v-card-text class="px-0">
      <v-row>
        <v-col cols="4">
          <date-picker
            v-model="datePicker.start"
            :masks="datePicker.masks"
            :min-date="datePicker.minimum"
          >
            <template #default="{ inputValue, inputEvents }">
              <v-text-field
                :value="inputValue"
                label="From Date"
                v-on="inputEvents"
                outlined
                clearable
                :rules="[
                  v => {
                    if (!datePicker.end) return true;
                    return !!v || 'Start Date Required';
                  }
                ]"
                hide-details
              />
            </template>
          </date-picker>
        </v-col>
        <v-col cols="4">
          <date-picker
            v-model="datePicker.end"
            :masks="datePicker.masks"
            :min-date="datePicker.start"
          >
            <template #default="{ inputValue, inputEvents }">
              <v-text-field
                :value="inputValue"
                label="To Date"
                v-on="inputEvents"
                outlined
                clearable
                :rules="[
                  v => {
                    if (!datePicker.end) return true;
                    return !!v || 'End Date Required';
                  }
                ]"
                hide-details
              />
            </template>
          </date-picker>
        </v-col>
        <v-col cols="4">
          <v-combobox
            v-model="selectedVehicle"
            :items="vehicles"
            item-text="device_name"
            item-value="id"
            label="Vehicle"
            multiple
            outlined
            required
            @change="(items) => { changeVehicle(items); checkAllVehicle(false) }"
          >
            <template #prepend-item>
              <v-list-item
                ripple
                @click="checkAllVehicle(true)"
              >
                <v-list-item-action class="mr-3">
                  <v-icon>{{ iconCheckAllVehicle }}</v-icon>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>
                    {{ textCheckAllVehicle }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider class="mt-2" />
            </template>
            <template #selection="{ item, index }">
              <v-chip v-if="index === 0">
                <span>{{ item.device_name }}</span>
              </v-chip>
              <span
                v-if="index === 1 && selectedVehicle.length > 1"
                class="grey--text caption"
              >
                (+{{ selectedVehicle.length - 1 }} Others)&nbsp;
              </span>
            </template>
          </v-combobox>
        </v-col>
      </v-row>
      <v-row>
        <v-col
          cols="12"
          class="py-0 d-flex justify-end"
        >
          <v-btn
            border
            color="primary"
            @click="generateReport"
          >
            Generate
          </v-btn>
        </v-col>
      </v-row>
      <v-row v-if="isGenerate && selectedVehicleForTabs.length">
        <v-col cols="12">
          <v-tabs
            bg-color="primary"
            v-model="selectedTab"
          >
            <v-tab
              v-for="tab in selectedVehicleForTabs"
              :key="tab.id"
              :value="tab.id"
              @click="generateReport(tab.id)"
            >
              {{ tab.device_name }}
            </v-tab>
          </v-tabs>
          <v-data-table
            :headers="headers"
            :items="dataPerTab"
            class="font-weight-light pa-5"
            :loading="isLoading"
            loading-text="Loading"
            :footer-props="footerProps"
            :options.sync="paginationOptions"
          >
            <template #[`item.book_detail.start_geofence_id`]="{ item }">
              {{ getOriginName(item.book_detail) }}
            </template>
            <template #[`item.book_detail.vehicle_name`]="{ item }">
              {{ getVehicleName(item.book_detail) }}
            </template>
            <template #[`item.book_detail.end_geofence_id`]="{ item }">
              {{ getDestinationName(item.book_detail) }}
            </template>
            <template #[`item.book_detail.start_datetime`]="{ item }">
              {{ startDateFormat(item.book_detail) }}
            </template>
            <template #[`item.book_detail.end_datetime`]="{ item }">
              {{ endDateFormat(item.book_detail) }}
            </template>
            <template #[`item.book_detail.created_at`]="{ item }">
              {{ formatDate(item.book_detail.created_at) }}
            </template>
          </v-data-table>
        </v-col>
      </v-row>
      <v-row v-if="isLoading && selectedVehicleForTabs.length === 0">
        <v-col cols="12">
          <p class="text-center">
            Loading
          </p>
        </v-col>
      </v-row>
      <v-row v-if="isGenerate && !isLoading && selectedVehicleForTabs.length === 0">
        <v-col cols="12">
          <p class="text-center">
            No Data
          </p>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import moment from "moment";
import isUndefined from "lodash/isUndefined";
import isNull from "lodash/isNull";
import get from "lodash/get";

export default {
  data() {
    return {
      vehicles: [],
      assetTypes: [],
      geofences: [],
      selectedVehicle: [],
      selectedVehicleForTabs: [],
      datePicker: {
        start: '',
        startUnix: '',
        paramStart: '',
        end: '',
        endUnix: '',
        paramEnd: '',
        masks: {
          input: 'DD MMMM YYYY'
        },
      },
      selectedTab: '',
      headers: [
        { text: "Booking ID", value: "book_detail.id" },
        { text: 'First Name', align: 'left', value: 'first_name' },
        { text: 'Last Name', align: 'left', value: 'last_name' },
        { text: "From", value: "book_detail.start_geofence_id" },
        { text: "To", value: "book_detail.end_geofence_id" },
        { text: "Start Date", value: "book_detail.start_datetime" },
        { text: "End Date", value: "book_detail.end_datetime" },
        { text: "Asset Name", value: "book_detail.vehicle_name" },
        { text: "Created At", value: "book_detail.created_at"},
      ],
      footerProps: {
        itemsPerPageOptions: [2, 3, 5, 10, 20, 50, 100, 200, 500]
      },
      paginationOptions: {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        sortDesc: []
      },
      data: [],
      isLoading: false,
      isGenerate: false,
      timeout: 0,
      textCheckAllVehicle: 'CHECK ALL VEHICLE',
      iconCheckAllVehicle: 'mdi-check-all',
    };
  },
  props: {
    params: {
      type: Object,
      required: false,
      default: () => ({
        vehicles: [],
        assetTypes: [],
        geofences: [],
      }),
    },
  },
  mounted() {
    this.vehicles = this.params.vehicles;
    this.assetTypes = this.params.assetTypes;
    this.geofences = this.params.geofences;
  },
  watch: {
    "datePicker.start"(value) {
      const startDate = moment(value).format("YYYY-MM-DD");
      this.datePicker.paramStart = startDate;
    },
    "datePicker.end"(value) {
      const endDate = moment(value).format("YYYY-MM-DD");
      this.datePicker.paramEnd = endDate;
    },
  },
  computed: {
    dataPerTab() { 
      const vehicle = this.selectedVehicleForTabs[this.selectedTab];
      return this.data.filter((item) => item.book_detail.vehicle_id == vehicle.id);
    },
  },
  methods: {
    checkAllVehicle(force = false) {
      if (force) {
        if (this.textCheckAllVehicle === 'CHECK ALL VEHICLE') {
          this.selectedVehicle = this.vehicles;
          this.textCheckAllVehicle = 'UNCHECK ALL VEHICLE';
          this.iconCheckAllVehicle = 'mdi-close';
        } else {
          this.selectedVehicle = [];
          this.textCheckAllVehicle = 'CHECK ALL VEHICLE';
          this.iconCheckAllVehicle = 'mdi-check-all';
        }
      } else {
        if (this.selectedVehicle.length === this.vehicles.length) {
          this.textCheckAllVehicle = 'UNCHECK ALL VEHICLE';
          this.iconCheckAllVehicle = 'mdi-close';
        } else {
          this.textCheckAllVehicle = 'CHECK ALL VEHICLE';
          this.iconCheckAllVehicle = 'mdi-check-all';
        }
      }
    },
    changeVehicle(items) {
      this.selectedVehicle = items.filter((vehicle) => typeof vehicle === 'object' && vehicle.id);
    },
    generateReport(vehicleId) {
      if (!this.selectedVehicle.length || !this.datePicker.start || !this.datePicker.end) return;
      if (this.selectedVehicle.length && this.datePicker.start && this.datePicker.end) {
        let selectedVehicleId = null;
        if (typeof vehicleId !== 'object') {
          selectedVehicleId = vehicleId;
        }
        clearTimeout(this.timeout);
        this.timeout = setTimeout(() => {
          this.isLoading = true;
          const promise = selectedVehicleId == null ? this.getDataBookTabs() : Promise.resolve();
          promise.then(() => {
            if (selectedVehicleId == null) {
              if (this.selectedVehicleForTabs.length) {
                this.selectedTab = 0;
                this.getDataBook(this.selectedVehicleForTabs[0].id);
              } else {
                this.data = [];
                this.isGenerate = true;
                this.isLoading = false;
              }
            } else {
              this.getDataBook(selectedVehicleId);
            }
          })
        }, 500);
      }
    },
    getDataBookTabs() {
      return this.$store.dispatch('API_CALL', {
        $method: 'get_book_for_tabs',
        $postData: {
          vehicle_id: this.selectedVehicle.map((vehicle) => vehicle.id),
          clientid: this.$store.state.clientid,
          start_date: this.datePicker.paramStart,
          end_date: this.datePicker.paramEnd,
          get_schedules: 1,
          get_recurring: 0,
        },
      })
      .then((response) => {
        this.selectedVehicleForTabs = response.data.data.map((vehicle) => this.selectedVehicle.find((v) => v.id === vehicle.vehicle_id));
      });
    },
    getDataBook(vehicleId) {
      this.isLoading = true;
      return this.$store.dispatch('API_CALL', {
        $method: 'get_all_book',
        $postData: {
          vehicle_id: vehicleId,
          clientid: this.$store.state.clientid,
          start_date: this.datePicker.paramStart,
          end_date: this.datePicker.paramEnd,
          get_schedules: 1,
          get_recurring: 0,
        },
      })
      .then((response) => {
        this.data = response.data.data;
        this.isGenerate = true;
        this.isLoading = false;
      });
    },
    getOriginName(item) {
      let destination = '';
      if (item.start_geofence_id) {
        this.geofences[this.$store.state.clientid]
          .forEach((group) => {
            const geofenceDestination = group.geofences.find(
              (geofence) =>
                parseInt(geofence.Id) === parseInt(item.start_geofence_id)
            );
            if (!isUndefined(geofenceDestination)) {
              destination = geofenceDestination.name;
            }
          });
      } else {
        try {
          const metadata = JSON.parse(item.metadata);
          destination = metadata.hasOwnProperty('address') ? metadata.address : '';
        } catch (e) {}
      }
      return destination;
    },
    getVehicleName(item) {
      if (item == null) return;
      let metadata = '';
      if (!isNull(item.vehicle_name)) {
        metadata = `Vehicle - ${item.vehicle_name}`;
      } else if (!isNull(item.asset_id)) {
        const typeName = this.assetTypes.find((d) => d.id == item.asset_type);
        metadata = `${get(typeName, 'name', '-')} - ${item.asset_number}`;
      } else {
        metadata = '--';
      }
      if (!metadata) return;
      return metadata;
    },
    getDestinationName(item) {
      let destination = '';
      if (item.end_geofence_id) {
        this.geofences[this.$store.state.clientid]
          .forEach((group) => {
            const geofenceDestination = group.geofences.find(
              (geofence) =>
                parseInt(geofence.Id) === parseInt(item.end_geofence_id)
            );
            if (!isUndefined(geofenceDestination)) {
              destination = geofenceDestination.name;
            }
          });
      } else {
        try {
          const metadata = JSON.parse(item.metadata);
          destination = metadata.hasOwnProperty('address') ? metadata.address : '';
        } catch (e) {}
      }
      return destination;
    },
    startDateFormat(item) {
      if (item == null) return;
      let date = null;
      if (item.hasOwnProperty("start_datetime")) {
        date = moment(item.start_datetime).format('ddd, D MMM YYYY HH:mm')
      }
      return date;
    },
    endDateFormat(item) {
      if (item == null) return;
      let date = null;
      if (item.hasOwnProperty("end_datetime")) {
        date = moment(item.end_datetime).format('ddd, D MMM YYYY HH:mm')
      }
      return date;
    },
    formatDate(date, format = 'D MMM YYYY HH:mm') {
      if (date == null) return;
      const dateFormatted = moment(date).format(format)
      return dateFormatted;
    },
  },
};
</script>
